import React, {
  ForwardedRef,
  ReactElement,
  forwardRef,
  useRef,
} from 'react';
import { RouteProps } from 'react-router-dom';
import StateCarousel from '../slider/state-carousel';
import useScrollToRef from '../../../utils/hooks/useScrollToRef';
import CityCarousel from '../slider/city-carousel';
import { SearchComponent } from './search';
import SearchResultsBlock from './search-results-block';

interface BreedSearchProps extends RouteProps {
  listingsType: string;
  states: string[];
  breedName: string;
  cities: string[];
  filteredStates: string[];
  filteredStateFullName: string;
}

const WrappedSearchResultsBlock = forwardRef((props: any, ref: ForwardedRef<HTMLDivElement>) => {
  return (
    <div className="filter-section">
      <div className="search-container" ref={ref}>
        <SearchResultsBlock {...props} />
      </div>
    </div>
  );
});

const WrappedCityCarousel = ({
  breed,
  cities,
  setSearchState,
  listingsType,
  onClick,
  filteredStates,
  filteredStateFullName,
}) => {
  const state = filteredStates.length > 0
    ? filteredStates[0]
    : '';

  if (!state) {
    return null;
  }

  return (
    <div className="search-container">
      <div className="container-filters mt-4">
        <h3 className="search-title mb-0">{`${breed} ${listingsType === 'puppy' ? 'Puppies' : 'Studs'} Available Now In ${filteredStateFullName}`}</h3>
        <CityCarousel
          state={state}
          cities={cities}
          onClick={(city) => {
            onClick();
            setSearchState((s) => ({
              ...s,
              page: 1, // We must manually reset pagination here, since we aren't using `refine`
              refinementList: {
                ...s.refinementList,
                state: [state],
                city: [city],
              },
            }));
          }}
        />
      </div>
    </div>
  );
};

const WrappedStateCarousel = ({
  breed,
  states,
  setSearchState,
  listingsType,
  onClick,
}) => (
  <div className="search-container">
    <div className="container-filters mt-4">
      <h3 className="search-title mb-0">{`${breed} ${listingsType === 'puppy' ? 'Puppies' : 'Studs'} Available Now In These States`}</h3>
      <StateCarousel
        states={states}
        onClick={(state) => {
          onClick();
          setSearchState((s) => ({
            ...s,
            page: 1, // We must manually reset pagination here, since we aren't using `refine`
            refinementList: {
              ...s.refinementList,
              state: [state],
            },
          }));
        }}
      />
    </div>
  </div>
);

const BreedSearch: React.FC<BreedSearchProps> = (props): ReactElement => {
  const {
    states,
    breedName,
    listingsType,
    cities,
    filteredStates,
    filteredStateFullName,
  } = props;

  const searchResultsRef = useRef(null);
  const scrollToSearchResults = useScrollToRef(searchResultsRef);

  return (
    <SearchComponent
      {...props}
      breedSlug={breedName}
      syncURL={true}
      instantRefinements={[
        'admin_breed_parents',
        'admin_single_breed',
      ]}
    >
      <WrappedSearchResultsBlock
        {...props}
        breedSlug={breedName}
        // Key needed because parent component maps through it's children
        key="search-results-block"
        ref={searchResultsRef}
      />
      {states.length > 0 && (
        // TODO: Type error here because the SearchComponent gives extra props to children
        // @ts-ignore next-line
        <WrappedStateCarousel
          {...props}
          breed={breedName}
          states={states}
          // Key needed because parent component maps through it's children
          key="state-carousel"
          onClick={scrollToSearchResults}
          listingsType={listingsType}
        />
      )}
      {cities.length > 0 && (
        // @ts-ignore next-line
        <WrappedCityCarousel
          {...props}
          breed={breedName}
          cities={cities}
          key="city-carousel"
          onClick={scrollToSearchResults}
          listingsType={listingsType}
          filteredStates={filteredStates}
          filteredStateFullName={filteredStateFullName}
        />
      )}
    </SearchComponent>
  );
};

export default BreedSearch;
